import React from 'react';

/**
 * Link Formatter Utility
 * Converts text containing URLs into JSX with clickable links
 */

// Enhanced URL detection regex that matches:
// - http://example.com
// - https://example.com  
// - www.example.com
// - example.com
// - ftp://example.com
const URL_REGEX = /(?:(?:https?|ftp):\/\/|www\.)[^\s<>"{}|\\^`[\]]+[^\s<>"{}|\\^`[\].,;:!?]/gi;

/**
 * Converts plain text with URLs into JSX with clickable links
 * @param {string} text - The text that may contain URLs
 * @param {Object} linkProps - Additional props to apply to links
 * @returns {JSX.Element} - JSX containing text and clickable links
 */
export const formatTextWithLinks = (text, linkProps = {}) => {
  if (!text || typeof text !== 'string') {
    return text;
  }

  const defaultLinkProps = {
    target: '_blank',
    rel: 'noopener noreferrer',
    style: {
      color: 'var(--primary-color, #3b82f6)',
      textDecoration: 'none'
    },
    onMouseEnter: (e) => {
      e.target.style.textDecoration = 'underline';
    },
    onMouseLeave: (e) => {
      e.target.style.textDecoration = 'none';
    },
    ...linkProps
  };

  // Split text by URLs and create JSX elements
  const parts = text.split(URL_REGEX);
  const matches = text.match(URL_REGEX) || [];
  
  const elements = [];
  
  for (let i = 0; i < parts.length; i++) {
    // Add text part
    if (parts[i]) {
      elements.push(parts[i]);
    }
    
    // Add link if there's a corresponding match
    if (matches[i]) {
      let url = matches[i];
      
      // Add protocol if missing
      if (!url.match(/^https?:\/\//i)) {
        if (url.startsWith('www.')) {
          url = 'https://' + url;
        } else if (url.match(/^[a-z0-9.-]+\.[a-z]{2,}$/i)) {
          url = 'https://' + url;
        }
      }
      
      elements.push(
        <a
          key={`link-${i}`}
          href={url}
          {...defaultLinkProps}
        >
          {matches[i]}
        </a>
      );
    }
  }
  
  return elements.length > 1 ? <>{elements}</> : elements[0] || text;
};

/**
 * Simple function to detect if text contains URLs
 * @param {string} text - Text to check
 * @returns {boolean} - True if text contains URLs
 */
export const hasLinks = (text) => {
  if (!text || typeof text !== 'string') return false;
  return URL_REGEX.test(text);
};

/**
 * Extract all URLs from text
 * @param {string} text - Text to extract URLs from
 * @returns {string[]} - Array of found URLs
 */
export const extractUrls = (text) => {
  if (!text || typeof text !== 'string') return [];
  return text.match(URL_REGEX) || [];
};